import { getAppMode } from '@databases/modes';
import {
  initializeSelectionsMachine,
  type SelectionUiInteraction,
} from '@databases/selections';
import {
  createDatabaseSelectionReducerPolicies,
  SelectionsApi,
  SelectionsApiAdapter,
  SelectionsApiReader,
} from '@databases/selections-api';
import { container, getService } from '@databases/service-container';
import { type SelectionLayoutDependencies } from '../layouts/selection-layout-dependencies.ts';

import { createKeyboardNavigationUsageTracker } from '../features/selections/create-keyboard-navigation-usage-tracker.ts';
import { createSelectionUiInteractionSink } from '../features/selections/create-selection-ui-interaction-sink.ts';
import {
  type SelectionFeatureActivationContext,
  SelectionFeatureKeys,
} from '../features/types.ts';
import { filterSelectionFeatures } from '../features/utils/filter-selection-features.ts';
import { resolveFeatures } from '../features/utils/resolve-features.ts';

import { getModeConfig } from './get-mode-config.ts';
import { type Recyclable, type RecyclableDisposeContext } from './types.ts';

/**
 * Owns the recyclable selection lifecycle. View and layout changes rebuild
 * feature composition without resetting database-scoped keyboard usage tracking.
 * The constructor-injected factory supplies the existing coordinate adapter once
 * per setup; permission rebuilds reuse it. The lifecycle contract stays unchanged.
 */
export function initializeRecyclableSelectionsMachine(
  getLayoutDependencies: (contentId: string) => SelectionLayoutDependencies | undefined,
): Recyclable {
  let destroy: VoidFunction | undefined;
  let unsubscribePermissions: VoidFunction | undefined;
  let removeSelectionUiDocumentListeners: VoidFunction | undefined;
  let keyboardNavigationUsageTracker:
    | ReturnType<typeof createKeyboardNavigationUsageTracker>
    | undefined;

  function initialize(
    currentContentId: string,
    dependencies: SelectionLayoutDependencies | undefined,
  ) {
    const permissionsManager = getService('PermissionsManager');

    if (!dependencies) {
      container.unregister('SelectionsApi');
      container.unregister('SelectionsActions');
      container.unregister('SelectionsReader');

      return;
    }
    const { databaseAdapter, viewChanges } = dependencies;

    removeSelectionUiDocumentListeners?.();

    const selectionUiSink = createSelectionUiInteractionSink({
      contentId: currentContentId,
      databaseAdapter,
      canEditDatabaseContent: () => permissionsManager.getPermissions().canEditContent,
    });

    const onSelectionUiInteraction = (event: SelectionUiInteraction) => {
      if (dependencies.selectionUiMetricsEnabled) {
        selectionUiSink.emit(event);
      }

      if (event.source === 'keyboard') {
        dependencies.scrollCellIntoView?.(event.detail.target);
        keyboardNavigationUsageTracker?.record(event.detail.actionFamily);
      }
    };

    const { setup, machine } = initializeSelectionsMachine({
      reducerPolicies: createDatabaseSelectionReducerPolicies({
        canEditContent: () =>
          dependencies.canEditContent &&
          permissionsManager.getPermissions().canEditContent,
        adapter: databaseAdapter,
      }),
    });

    const selectionsApi = new SelectionsApi({
      machine,
      adapter: databaseAdapter,
    });

    // Now that we have concrete instances, register them immediately.
    container.registerSingleton('SelectionsApi', () => selectionsApi);
    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({ selectionsApi });
    });
    container.registerSingleton('SelectionsReader', () => {
      return new SelectionsApiReader({
        selectionsApi,
        adapter: databaseAdapter,
      });
    });

    /**
     * Selection UI stabilized metrics use rAF; when the tab is hidden or the page unloads,
     * cancel only this sink's six in-flight refs **silently** (no Reliability rows) so
     * durations are not inflated across background time (see `createSelectionUiInteractionSink`).
     */
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        selectionUiSink.cancelInFlightMetrics();
      }
    };

    const onPageHide = () => {
      selectionUiSink.cancelInFlightMetrics();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    removeSelectionUiDocumentListeners = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };

    const modeConfig = getModeConfig(getAppMode());

    const stopFeatures = setup(({ inputCapture, sharedLoop }) => {
      /**
       * The mode determines which features are enabled.
       * There are also some items that despite the mode, are always enabled
       * as they are required for syncing changes, etc.
       */
      const { modeFeatures, alwaysOnFeatures } = modeConfig.buildSelectionFeatures({
        inputCapture,
        sharedLoop,
        databaseAdapter,
        viewChanges,
        contentId: currentContentId,
        onSelectionUiInteraction,
        mouseInputAdapter: dependencies.mouseInputAdapter,
      });

      const features = [
        ...modeFeatures.inputs,
        ...modeFeatures.effects,
        ...alwaysOnFeatures,
      ];
      const activeFeatureSet = new Set(
        dependencies.featureLayoutTypes.flatMap((layoutType) => {
          const context: SelectionFeatureActivationContext = {
            permissions: permissionsManager.getPermissions(),
            layoutType,
          };

          return filterSelectionFeatures(features, context);
        }),
      );
      const activeFeatures = features.filter((feature) => activeFeatureSet.has(feature));

      if (
        activeFeatures.some(
          (feature) =>
            feature.key === SelectionFeatureKeys.TABLE_NAVIGATION_KEYBOARD_INPUT ||
            feature.key === SelectionFeatureKeys.TABLE_TAB_NAVIGATION_KEYBOARD_INPUT,
        )
      ) {
        keyboardNavigationUsageTracker?.markAvailable();
      }

      return resolveFeatures(activeFeatures);
    });

    destroy = () => {
      removeSelectionUiDocumentListeners?.();
      removeSelectionUiDocumentListeners = undefined;
      selectionUiSink.dispose();
      stopFeatures();
    };
  }

  return {
    recycleOn: ['view-id-changed', 'layout-type-changed'],

    setup(contentId) {
      const dependencies = getLayoutDependencies(contentId);

      keyboardNavigationUsageTracker ??= createKeyboardNavigationUsageTracker({
        contentId,
      });

      initialize(contentId, dependencies);

      // Permission changes affect only selection feature policy, so this inner lifecycle stays local.
      unsubscribePermissions?.();

      const permissionsManager = getService('PermissionsManager');

      unsubscribePermissions = permissionsManager.subscribe(() => {
        destroy?.();
        initialize(contentId, dependencies);
      });
    },

    dispose(context?: RecyclableDisposeContext) {
      unsubscribePermissions?.();
      unsubscribePermissions = undefined;
      destroy?.();
      destroy = undefined;

      if (context?.reason !== 'view-identity-changed') {
        keyboardNavigationUsageTracker?.dispose();
        keyboardNavigationUsageTracker = undefined;
      }
    },
  };
}
